
import {
  defineComponent, PropType, ref, watch, computed, onMounted, shallowRef, onUnmounted, nextTick,
} from 'vue';
import { useI18n } from 'vue-i18n';
import Button from 'primevue/button';
import Message from 'primevue/message';
import type { FlowOperatorMetadata, NodeWarning, WarningValidationContext } from '@/types/flow-types';
import { useStore } from '@/store';
import { UserState } from '@/composables/User';
import { TemplateEditorState, resetWhatsappHashChanged } from '@/composables/template-editor/TemplateEditor';
import { WhatsappValidationStatus } from '@/composables/flows/useWhatsappTemplateValidation';

export default defineComponent({
  name: 'FlowOperatorForm',

  components: {
    Button,
    Message,
  },

  props: {
    operator: {
      type: Object as PropType<FlowOperatorMetadata>,
      required: true,
    },

    modelValue: {
      type: Object as PropType<any>,
      default: null,
    },

    isEditMode: {
      type: Boolean,
      default: false,
    },

    isReplaceMode: {
      type: Boolean,
      default: false,
    },

    operatorId: {
      type: String,
      default: undefined,
    },
  },

  emits: ['update:modelValue', 'submit', 'cancel', 'close', 'update-step'],

  setup(props, { emit, expose }) {
    const { t } = useI18n();
    const store = useStore();
    const localData = ref<any>(props.modelValue ?? props.operator.defaultData(undefined, store.getters['flowEditor/getActiveFlow']?.type));
    const validationErrors = shallowRef<any>({});
    const isValidating = ref(false);
    const isMounted = ref(true);
    // Save asked by the user, in progress
    const isSaving = ref(false);
    // Data sent to the panel, which closes: the save button keeps showing the save until then
    const isSubmitted = ref(false);

    onUnmounted(() => {
      isMounted.value = false;
    });

    // Get campaign info from store
    const activeFlow = computed(() => store.getters['flowEditor/getActiveFlow']);
    const campaignId = computed(() => activeFlow.value?.id ?? 0);
    const campaignType = computed(() => activeFlow.value?.type ?? '');
    const shopId = computed(() => UserState.activeShop?.id ?? 0);

    watch(localData, (newData) => {
      emit('update:modelValue', newData);
    }, { deep: true });

    watch(() => props.modelValue, (newValue) => {
      if (newValue !== localData.value) {
        localData.value = newValue;
      }
    });

    const submitButtonLabel = computed(() => {
      if (isSaving.value || isSubmitted.value) return t('flows.operatorPanel.saving');
      if (props.isEditMode) return t('flows.operatorPanel.saveStep');
      if (props.isReplaceMode) return t('flows.operatorPanel.replaceStep');
      return t('flows.operatorPanel.addStep');
    });

    // Run validation (used on mount and on submit)
    const runValidation = async (submitOnSuccess = false) => {
      if (!props.operator.validate) {
        if (submitOnSuccess) {
          await executeCallbackAndSubmit();
        }
        return;
      }

      isValidating.value = true;
      let submitted = false;

      try {
        const context = { shopId: shopId.value };
        const result = await props.operator.validate(localData.value, context);

        if (!isMounted.value) return;

        // Optimization: Don't set errors if we are about to submit and close
        // This avoids triggering prop updates on a component that will be unmounted
        if (!submitOnSuccess || !result.success) {
          validationErrors.value = result.validate || {};
        }

        if (result.success) {
          if (submitOnSuccess) {
            submitted = true;
            await executeCallbackAndSubmit();
          }
        } else {
          // Scroll to first error input after DOM update
          setTimeout(() => {
            const firstErrorElement = document.querySelector('.has-error');
            if (firstErrorElement) {
              firstErrorElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
            }
          }, 100);
        }
      } catch (error) {
        console.error('Validation error:', error);
        if (isMounted.value) {
          validationErrors.value = {};
        }
      } finally {
        if (isMounted.value && !submitted) {
          isValidating.value = false;
        }
      }
    };

    /**
     * Execute operator callback (if exists) and then submit
     * This is used for operators like WhatsApp that need to submit templates for validation
     */
    const executeCallbackAndSubmit = async () => {
      let dataToSubmit = localData.value;

      // Execute callback if operator has one (e.g., WhatsApp template submission)
      // Skip if:
      // 1. WhatsApp was already resubmitted after template editor close
      // 2. WhatsApp is already approved in edit mode (no changes made)
      const isWhatsappAlreadyApproved = props.operator.id === 'boxsendwhatsapp'
        && props.isEditMode
        && localData.value.template_validation_status === WhatsappValidationStatus.APPROVED;
      const shouldSkipCallback = props.operator.id === 'boxsendwhatsapp'
        && (whatsappAlreadyResubmitted.value || isWhatsappAlreadyApproved);
      if (props.operator.callback && !shouldSkipCallback) {
        try {
          const context = { shopId: shopId.value, skipValidation: false };
          const callbackResult = await props.operator.callback(localData.value, context);
          // Update local data with callback result only if it returns data (e.g., WhatsApp validation status)
          // Some callbacks like SendEmail return void and should not overwrite the data
          if (callbackResult !== undefined) {
            dataToSubmit = callbackResult;
            localData.value = dataToSubmit;
          }
        } catch (error) {
          console.error('Operator callback error:', error);
          // Continue with submission even if callback fails
        }
      }

      isSubmitted.value = true;
      emit('submit', dataToSubmit);
    };

    // BaseInputText, behind most text fields, only emits its value 500 ms after the last keystroke:
    // saving or checking for changes right after typing must wait for it, or the last edit is lost.
    const FIELD_EMIT_DELAY_MS = 500;
    let lastFieldInputAt = 0;

    const onFieldInput = () => {
      lastFieldInputAt = Date.now();
    };

    const settlePendingInput = async () => {
      const remaining = lastFieldInputAt + FIELD_EMIT_DELAY_MS - Date.now();
      if (remaining > 0) {
        await new Promise((resolve) => { setTimeout(resolve, remaining + 50); });
      }
      await nextTick();
    };

    // Validation started on mount (edit mode): a submit waits for it instead of being dropped
    let mountValidation: Promise<void> = Promise.resolve();

    // WhatsApp template resubmitted after the template editor, and banner actions ("Submit for validation")
    const isResubmitting = ref(false);
    const isWarningActionLoading = ref(false);

    // The save button is disabled meanwhile: a save asked from the panel's dialog is ignored too
    const isBusy = () => isSaving.value || isResubmitting.value || isWarningActionLoading.value;

    const handleSubmit = async () => {
      if (isBusy()) {
        return;
      }
      isSaving.value = true;
      // Keeps the button disabled while waiting
      isValidating.value = true;
      try {
        await mountValidation;
        // The mount validation clears the flag when it ends
        isValidating.value = true;
        await settlePendingInput();
        await runValidation(true);
      } finally {
        isSaving.value = false;
      }
    };

    // Used by FlowOperatorPanel to save or check for changes when the panel is being closed
    expose({ handleSubmit, settlePendingInput, isBusy });

    // Run validation when form opens (edit mode)
    onMounted(() => {
      if (props.isEditMode) {
        mountValidation = runValidation(false);
      }
    });

    // Form-level warnings computed from operator validators
    const formWarnings = ref<NodeWarning[]>([]);

    const computeFormWarnings = async () => {
      if (!props.operator.validators || !props.operator.validators.length) {
        formWarnings.value = [];
        return;
      }

      const context: WarningValidationContext = {
        operatorId: props.operator.id,
        data: localData.value,
        scenarioType: campaignType.value as any,
        parentIds: [],
        parentOperators: [],
        shopId: shopId.value,
      };

      const warnings: NodeWarning[] = [];
      for (const validator of props.operator.validators) {
        try {
          const result = await validator.validate(localData.value, context);
          if (result) {
            const items = Array.isArray(result) ? result : [result];
            // Only show warnings flagged for form display
            for (const w of items) {
              if (w.showInForm) {
                warnings.push(w);
              }
            }
          }
        } catch (e) {
          // Skip failed validators
        }
      }
      formWarnings.value = warnings;
    };

    const handleFormWarningAction = async (event: string) => {
      if (event === 'submit-whatsapp-validation') {
        if (!shopId.value || !localData.value?.template_display_selector_id) return;

        isWarningActionLoading.value = true;
        try {
          const { SubmitWhatsappValidation } = await import('@/composables/services/Whatsapp');
          const response = await SubmitWhatsappValidation(shopId.value, localData.value);

          let newStatus = WhatsappValidationStatus.ERROR;
          if (response.success && response.data && response.data.template) {
            const { status } = response.data.template;
            newStatus = ((WhatsappValidationStatus as any)[status] || WhatsappValidationStatus.PENDING) as WhatsappValidationStatus;
          }

          localData.value = {
            ...localData.value,
            template_validation_status: newStatus,
          };

          // Persist status change on the step immediately so it survives cancel
          if (props.operatorId) {
            emit('update-step', {
              stepId: props.operatorId,
              data: { template_validation_status: newStatus },
            });
          }
        } catch (error) {
          console.error('[WhatsApp Submit] Failed to submit for validation:', error);
          localData.value = {
            ...localData.value,
            template_validation_status: WhatsappValidationStatus.ERROR,
          };
        } finally {
          isWarningActionLoading.value = false;
        }
      }
    };

    // Recompute warnings when data changes (only in edit mode)
    watch(localData, () => {
      if (props.isEditMode) {
        computeFormWarnings();
      }
    }, { deep: true, immediate: true });

    // Track if WhatsApp was already resubmitted (to skip on form save)
    const whatsappAlreadyResubmitted = ref(false);

    // Watch for template editor close to check if WhatsApp hash changed
    const needRevalidation = computed(() => store.getters['liveEditor/getNeedRevalidation']);

    watch(() => TemplateEditorState.isTemplateEditorVisible, async (isVisible) => {
      // When template editor closes and hash changed, resubmit WhatsApp template
      if (!isVisible && needRevalidation.value && props.operator.id === 'boxsendwhatsapp') {
        store.commit('liveEditor/setNeedRevalidation', false);
        if (props.operator.callback) {
          isResubmitting.value = true;
          try {
            // Reset validation status to NOT_SUBMITTED before resubmitting
            localData.value.template_validation_status = WhatsappValidationStatus.NOT_SUBMITTED;

            // Call the callback to resubmit the template
            const context = { shopId: shopId.value, skipValidation: false };
            const result = await props.operator.callback(localData.value, context);

            if (result) {
              localData.value = result;
              emit('update:modelValue', result);
              // Mark as already resubmitted to skip on form save
              whatsappAlreadyResubmitted.value = true;
            }
          } catch (error) {
            console.error('WhatsApp resubmission error:', error);
          } finally {
            isResubmitting.value = false;
          }
        }

        resetWhatsappHashChanged();
      }
    });

    return {
      t,
      localData,
      validationErrors,
      isValidating,
      isResubmitting,
      handleSubmit,
      onFieldInput,
      submitButtonLabel,
      campaignId,
      campaignType,
      shopId,
      formWarnings,
      isWarningActionLoading,
      handleFormWarningAction,
    };
  },
});
